import { DetailedHTMLProps, InputHTMLAttributes, useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Datepicker, Input, InputError } from "@monorepo/components";
import { CalendarIcon } from "@monorepo/icons";
import theme from "@monorepo/styles/theme";
import { IServiceParameter, ParameterTypeEnum } from "@monorepo/types";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import debounce from "lodash.debounce";

import { StyledInput } from "./ParametersInputFields.styles";

export const placeholdersMap = {
  [ParameterTypeEnum.CURRENCY]: "0.00",
  [ParameterTypeEnum.INTEGER]: undefined,
  [ParameterTypeEnum.INTEGER_WITH_ZERO]: undefined,
  [ParameterTypeEnum.IBAN]: "LT XX XXXX XXXX XXXX XXXX",
  [ParameterTypeEnum.LIST]: undefined,
  [ParameterTypeEnum.DATE]: "",
  [ParameterTypeEnum.STRING]: "",
} as const;

type TProps = DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement> & {
  parameter: IServiceParameter;
  error?: string;
  disallowZero?: boolean;
  isThirdPartyPE?: boolean;
  isCounter?: boolean;
  onCalculate?: () => void;
};

export const ParameterInput: React.FC<TProps> = ({
  parameter,
  error,
  autoFocus,
  disallowZero,
  isThirdPartyPE,
  isCounter,
  onCalculate,
}) => {
  const {
    parameter_name: name,
    read_only: readOnly,
    api_parameter_label: label,
    service_parameter_id: parameterId,
    value,
    hidden,
    parameter_type: type,
    dropdown_items: dropdownItems,
  } = parameter;

  const placeholder = placeholdersMap[type as keyof typeof placeholdersMap];
  const { register, setValue, control } = useFormContext();
  const [inputVal, setInputVal] = useState(value || (readOnly ? "-" : ""));
  const { t } = useTranslation();
  const [debouncedValue, setDebouncedValue] = useState<string | undefined>();
  const debounceHandler = useMemo(() => debounce((v) => setDebouncedValue(v), 750), []);

  useEffect(() => {
    setValue(name, value || (readOnly ? "-" : ""));
    setInputVal(value || (readOnly ? "-" : ""));
  }, [name, value, setValue, readOnly, parameter]);

  useEffect(() => {
    if (
      isThirdPartyPE &&
      isCounter &&
      onCalculate &&
      (!!debouncedValue || debouncedValue === "" || debouncedValue === "0")
    ) {
      onCalculate();
    }
  }, [isCounter, isThirdPartyPE, debouncedValue, onCalculate]);

  const [selectValue, setSelectValue] = useState<string | undefined>(value);

  const onChange = (e: SelectChangeEvent) => {
    setSelectValue(e.target.value);
  };

  if (type === ParameterTypeEnum.LIST && dropdownItems && dropdownItems.length > 0) {
    return (
      <Box
        key={name}
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          justifyItems: "space-between",
          "& label": { padding: "6px 6px 3px 3px" },
        }}
      >
        <InputLabel
          sx={{
            whiteSpace: "break-spaces",
            maxWidth: "100%",
            fontSize: theme.typography.inputLabel.fontSize,
            color: theme.palette.text.secondary,
          }}
          htmlFor={name}
        >
          {parameter.api_parameter_label}
        </InputLabel>
        <Select
          sx={{
            maxWidth: {
              sm: "340px",
            },
          }}
          disabled={readOnly || false}
          MenuProps={{
            PaperProps: {
              sx: {
                padding: 1,
                "& .MuiButtonBase-root.MuiMenuItem-root:hover": {
                  bgcolor: theme.palette.shuttleGray.main,
                  color: theme.palette.primary.contrastText,
                  borderRadius: "5px",
                },
              },
            },
          }}
          input={<StyledInput />}
          variant="standard"
          id={name}
          {...register(name)}
          onChange={onChange}
          value={selectValue}
        >
          {dropdownItems.map(({ item_identifier: itemIdentifier, item_description: itemDescription }) => (
            <MenuItem
              sx={{
                minWidth: "0px",
              }}
              key={itemIdentifier}
              value={itemIdentifier}
            >
              <Box
                sx={{
                  height: "100%",
                  verticalAlign: "middle",
                  display: "inline-block",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                  width: "100%",
                  maxWidth: {
                    xs: "290px",
                    sm: "400px",
                    lg: "600px",
                  },
                }}
              >
                {itemDescription}
              </Box>
            </MenuItem>
          ))}
        </Select>
        {error && <InputError>{error}</InputError>}
      </Box>
    );
  }
  if (type === ParameterTypeEnum.DATE) {
    return (
      <Datepicker
        defaultValue={new Date()}
        disabledDirectInput
        control={control}
        disabled={readOnly}
        label={label}
        error={error}
        name={name}
        id={name}
        adornment={
          <IconButton aria-label={t("aria.open_calendar")}>
            <CalendarIcon fontSize="small" sx={{ width: 15, height: 15, overflow: "visible  " }} />
          </IconButton>
        }
      />
    );
  }
  return (
    <Input
      {...register(name)}
      tabIndex={readOnly ? -1 : 0}
      key={parameterId}
      type="text"
      hidden={hidden}
      id={name}
      name={name}
      label={label}
      readOnly={readOnly}
      error={error}
      placeholder={placeholder}
      autoFocus={autoFocus}
      isCounter={!!isCounter}
      inputVal={inputVal}
      isThirdPartyPE={isThirdPartyPE}
      autoComplete="off"
      autoCorrect="off"
      autoCapitalize="off"
      spellCheck={false}
      onChange={(e: any) => {
        const formatedValue =
          type === ParameterTypeEnum.CURRENCY || type === ParameterTypeEnum.FLOAT
            ? formatCurrencyFloatModel(e.target.value)
            : e.target.value;

        setValue(name, formatedValue);
        setInputVal(formatedValue);
        if (inputVal !== formatedValue) {
          debounceHandler(formatedValue);
        }
      }}
      onFocus={() => {
        if (disallowZero && value === "0") {
          setValue(name, "");
          setInputVal("");
          if (inputVal !== "") {
            debounceHandler("");
          }
        }
      }}
    />
  );
};

const formatCurrencyFloatModel = (value: string) => {
  return value.replaceAll(",", ".");
};
